const STORAGE_KEY = "shuttlescore.roomRecents.v1";
const MAX_PER_USER = 24;

function readAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/**
 * @typedef {{
 *   room: string;
 *   roomDisplayName: string;
 *   role?: 'referee' | 'spectator';
 *   isCreator?: boolean;
 *   creatorPlaysInSession?: boolean;
 *   lastVisitedAt: number;
 *   firstJoinedAt?: number;
 *   hidden?: boolean;
 * }} RoomRecentEntry
 */

/**
 * @param {string} uid
 * @returns {RoomRecentEntry[]}
 */
export function readRoomRecents(uid) {
  if (!uid) return [];
  const list = readAll()[uid];
  if (!Array.isArray(list)) return [];
  return list.filter((e) => e && typeof e.room === "string");
}

/**
 * @param {string} uid
 * @param {Omit<RoomRecentEntry, 'lastVisitedAt' | 'hidden'> & { lastVisitedAt?: number }} entry
 */
export function recordRoomRecent(uid, entry) {
  if (!uid || !entry?.room) return;
  const now = entry.lastVisitedAt ?? Date.now();
  const all = readAll();
  const prev = readRoomRecents(uid);
  const prior = prev.find((e) => e.room === entry.room);
  const firstJoinedAt = prior?.firstJoinedAt ?? entry.firstJoinedAt ?? now;
  const next = [
    {
      room: entry.room,
      roomDisplayName: (entry.roomDisplayName || entry.room).trim() || entry.room,
      role: entry.role === "referee" ? "referee" : "spectator",
      isCreator: Boolean(entry.isCreator),
      creatorPlaysInSession: Boolean(entry.isCreator) && entry.creatorPlaysInSession !== false,
      lastVisitedAt: now,
      firstJoinedAt,
      hidden: false,
    },
    ...prev.filter((e) => e.room !== entry.room),
  ].slice(0, MAX_PER_USER);
  all[uid] = next;
  writeAll(all);
}

/**
 * Bump visit time for the room the player just left (keeps Resume in sync).
 * @param {string} uid
 * @param {{ room: string; roomDisplayName?: string; role?: string; registerAsCreator?: boolean; creatorPlaysInSession?: boolean }} session
 */
export function touchRoomRecentFromSession(uid, session) {
  if (!uid || !session?.room) return;
  const prior = readRoomRecents(uid).find((e) => e.room === session.room);
  recordRoomRecent(uid, {
    room: session.room,
    roomDisplayName: session.roomDisplayName || session.room,
    role: session.role === "referee" ? "referee" : "spectator",
    isCreator: Boolean(session.registerAsCreator ?? prior?.isCreator),
    creatorPlaysInSession:
      session.creatorPlaysInSession !== false && prior?.creatorPlaysInSession !== false,
    firstJoinedAt: prior?.firstJoinedAt,
  });
}

/**
 * Hide a room from the local picker (non-creators or after archive).
 * @param {string} uid
 * @param {string} room
 */
export function hideRoomRecent(uid, room) {
  if (!uid || !room) return;
  const all = readAll();
  const list = readRoomRecents(uid).map((e) =>
    e.room === room ? { ...e, hidden: true } : e
  );
  all[uid] = list;
  writeAll(all);
}

/**
 * @param {string} uid
 * @returns {RoomRecentEntry | null}
 */
export function pickPrimaryResumableRoom(uid) {
  const list = readRoomRecents(uid).filter((e) => !e.hidden);
  if (!list.length) return null;
  return [...list].sort((a, b) => (b.lastVisitedAt || 0) - (a.lastVisitedAt || 0))[0];
}

/** @param {RoomRecentEntry | null | undefined} entry */
export function roomRecentToResumeRow(entry) {
  if (!entry?.room) return null;
  return {
    room: entry.room,
    roomDisplayName: entry.roomDisplayName || entry.room,
    isCreator: Boolean(entry.isCreator),
    lastVisitedAt: entry.lastVisitedAt || 0,
    firstJoinedAtMs: entry.firstJoinedAt || entry.lastVisitedAt || 0,
  };
}

export function clearRoomRecentsForUser(uid) {
  if (!uid) return;
  const all = readAll();
  delete all[uid];
  writeAll(all);
}
